import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Eye, EyeOff, BarChart3, Wrench, Megaphone, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HUB_ICONS = {
  sales: BarChart3,
  operations: Wrench,
  marketing: Megaphone,
};

export const PageManager = () => {
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filterHub, setFilterHub] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editFormData, setEditFormData] = useState({
    page_key: '',
    title: '',
    description: '',
    path: '',
    is_enabled: true,
    icon: '',
    order: 0,
    allowed_roles: [],
    hub: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pagesRes, rolesRes] = await Promise.all([
        axios.get(`${API}/pages`),
        axios.get(`${API}/roles`)
      ]);
      
      setPages(pagesRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedPages = async () => {
    if (!window.confirm('This will seed default pages if none exist. Continue?')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/pages/seed`);
      toast.success(response.data.message);
      await fetchData();
    } catch (error) {
      console.error('Error seeding pages:', error);
      toast.error(error.response?.data?.detail || 'Failed to seed pages');
    }
  };

  const handleCreatePage = () => {
    setEditFormData({
      page_key: '',
      title: '',
      description: '',
      path: '',
      is_enabled: true,
      icon: 'FileText',
      order: pages.length,
      allowed_roles: [],
      hub: '',
    });
    setCreateDialogOpen(true);
  };

  const handleEditPage = (page) => {
    setSelectedPage(page);
    setEditFormData({
      page_key: page.page_key,
      title: page.title,
      description: page.description || '',
      path: page.path,
      is_enabled: page.is_enabled,
      icon: page.icon || '',
      order: page.order,
      allowed_roles: page.allowed_roles || [],
      hub: page.hub || '',
    });
    setEditDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setEditFormData({
      ...editFormData,
      [field]: value,
    });
  };

  const handleCreateNewPage = async () => {
    try {
      if (!editFormData.page_key || !editFormData.title || !editFormData.path) {
        toast.error('Please fill in page key, title, and path');
        return;
      }

      const createData = {
        page_key: editFormData.page_key,
        title: editFormData.title,
        description: editFormData.description,
        path: editFormData.path,
        is_enabled: editFormData.is_enabled,
        icon: editFormData.icon,
        order: parseInt(editFormData.order),
        allowed_roles: editFormData.allowed_roles,
        hub: editFormData.hub || null,
      };

      await axios.post(`${API}/pages`, createData);
      toast.success('Page created successfully!');
      setCreateDialogOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error creating page:', error);
      toast.error(error.response?.data?.detail || 'Failed to create page');
    }
  };

  const handleSavePage = async () => {
    try {
      const updateData = {
        title: editFormData.title,
        description: editFormData.description,
        path: editFormData.path,
        is_enabled: editFormData.is_enabled,
        icon: editFormData.icon,
        order: parseInt(editFormData.order),
        allowed_roles: editFormData.allowed_roles,
        hub: editFormData.hub || null,
      };

      await axios.put(`${API}/pages/${selectedPage.page_key}`, updateData);
      toast.success('Page updated successfully');
      setEditDialogOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error updating page:', error);
      toast.error('Failed to update page');
    }
  };

  const handleToggleEnabled = async (page) => {
    try {
      await axios.put(`${API}/pages/${page.page_key}`, {
        is_enabled: !page.is_enabled
      });
      toast.success(`Page ${!page.is_enabled ? 'enabled' : 'disabled'}`);
      await fetchData();
    } catch (error) {
      console.error('Error toggling page:', error);
      toast.error('Failed to update page');
    }
  };

  const getFilteredPages = () => {
    if (filterHub === 'all') return pages;
    if (filterHub === 'root') return pages.filter(p => !p.hub);
    return pages.filter(p => p.hub === filterHub);
  };

  const groupedPages = () => {
    const filtered = getFilteredPages();
    const grouped = {
      root: [],
      sales: [],
      operations: [],
      marketing: [],
    };

    filtered.forEach(page => {
      if (!page.hub) {
        grouped.root.push(page);
      } else {
        grouped[page.hub].push(page);
      }
    });

    return grouped;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading page manager...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const grouped = groupedPages();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">Page Manager</h1>
              <p className="text-muted-foreground">Manage page visibility, titles, and access control</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleSeedPages}>
              <Plus className="h-4 w-4 mr-2" />
              Seed Defaults
            </Button>
            <Button onClick={handleCreatePage}>
              <Plus className="h-4 w-4 mr-2" />
              Create Page
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Pages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pages.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Enabled</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {pages.filter(p => p.is_enabled).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Disabled</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {pages.filter(p => !p.is_enabled).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Restricted</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {pages.filter(p => p.allowed_roles && p.allowed_roles.length > 0).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card>
          <CardHeader>
            <CardTitle>Filter Pages</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={filterHub} onValueChange={setFilterHub}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pages</SelectItem>
                <SelectItem value="root">Root Pages</SelectItem>
                <SelectItem value="sales">Sales Hub</SelectItem>
                <SelectItem value="operations">Operations Hub</SelectItem>
                <SelectItem value="marketing">Marketing Hub</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Pages Table */}
        <Card>
          <CardHeader>
            <CardTitle>Pages</CardTitle>
            <CardDescription>Manage all platform pages</CardDescription>
          </CardHeader>
          <CardContent>
            {filterHub === 'all' ? (
              // Show grouped view
              <div className="space-y-6">
                {/* Root Pages */}
                {grouped.root.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Root Pages</h3>
                    <PageTable pages={grouped.root} onEdit={handleEditPage} onToggle={handleToggleEnabled} roles={roles} />
                  </div>
                )}

                {/* Sales Hub */}
                {grouped.sales.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Sales Hub
                    </h3>
                    <PageTable pages={grouped.sales} onEdit={handleEditPage} onToggle={handleToggleEnabled} roles={roles} />
                  </div>
                )}

                {/* Operations Hub */}
                {grouped.operations.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Wrench className="h-5 w-5" />
                      Operations Hub
                    </h3>
                    <PageTable pages={grouped.operations} onEdit={handleEditPage} onToggle={handleToggleEnabled} roles={roles} />
                  </div>
                )}

                {/* Marketing Hub */}
                {grouped.marketing.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Megaphone className="h-5 w-5" />
                      Marketing Hub
                    </h3>
                    <PageTable pages={grouped.marketing} onEdit={handleEditPage} onToggle={handleToggleEnabled} roles={roles} />
                  </div>
                )}
              </div>
            ) : (
              // Show filtered view
              <PageTable pages={getFilteredPages()} onEdit={handleEditPage} onToggle={handleToggleEnabled} roles={roles} />
            )}
          </CardContent>
        </Card>

        {/* Create Page Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Page</DialogTitle>
              <DialogDescription>Add a new page to the platform navigation</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Page Key * (unique identifier)</Label>
                <Input
                  value={editFormData.page_key}
                  onChange={(e) => handleFormChange('page_key', e.target.value)}
                  placeholder="my-custom-page"
                />
                <p className="text-xs text-muted-foreground">Lowercase, use dashes instead of spaces</p>
              </div>

              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={editFormData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="My Custom Page"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editFormData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="Page description"
                />
              </div>

              <div className="space-y-2">
                <Label>Path *</Label>
                <Input
                  value={editFormData.path}
                  onChange={(e) => handleFormChange('path', e.target.value)}
                  placeholder="/my-custom-page"
                />
                <p className="text-xs text-muted-foreground">Must start with /</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Icon Name</Label>
                  <Input
                    value={editFormData.icon}
                    onChange={(e) => handleFormChange('icon', e.target.value)}
                    placeholder="FileText"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={editFormData.order}
                    onChange={(e) => handleFormChange('order', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Hub</Label>
                  <Select
                    value={editFormData.hub || 'none'}
                    onValueChange={(val) => handleFormChange('hub', val === 'none' ? null : val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Root (No Hub)</SelectItem>
                      <SelectItem value="sales">Sales Hub</SelectItem>
                      <SelectItem value="operations">Operations Hub</SelectItem>
                      <SelectItem value="marketing">Marketing Hub</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={editFormData.is_enabled}
                  onCheckedChange={(checked) => handleFormChange('is_enabled', checked)}
                />
                <Label>Page Enabled (visible in navigation)</Label>
              </div>

              <div className="space-y-2">
                <Label>Allowed Roles (leave empty for all roles)</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                  {roles.map(role => (
                    <Badge
                      key={role.id}
                      variant={editFormData.allowed_roles.includes(role.name) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        const newRoles = editFormData.allowed_roles.includes(role.name)
                          ? editFormData.allowed_roles.filter(r => r !== role.name)
                          : [...editFormData.allowed_roles, role.name];
                        handleFormChange('allowed_roles', newRoles);
                      }}
                    >
                      {role.display_name || role.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateNewPage}>
                Create Page
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Page: {selectedPage?.page_key}</DialogTitle>
              <DialogDescription>Update page metadata and access control</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editFormData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editFormData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Path</Label>
                <Input
                  value={editFormData.path}
                  onChange={(e) => handleFormChange('path', e.target.value)}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon Name</Label>
                  <Input
                    value={editFormData.icon}
                    onChange={(e) => handleFormChange('icon', e.target.value)}
                    placeholder="LayoutDashboard"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={editFormData.order}
                    onChange={(e) => handleFormChange('order', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={editFormData.is_enabled}
                  onCheckedChange={(checked) => handleFormChange('is_enabled', checked)}
                />
                <Label>Page Enabled (visible in navigation)</Label>
              </div>

              <div className="space-y-2">
                <Label>Allowed Roles (leave empty for all roles)</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                  {roles.map(role => (
                    <Badge
                      key={role.id}
                      variant={editFormData.allowed_roles.includes(role.name) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        const newRoles = editFormData.allowed_roles.includes(role.name)
                          ? editFormData.allowed_roles.filter(r => r !== role.name)
                          : [...editFormData.allowed_roles, role.name];
                        handleFormChange('allowed_roles', newRoles);
                      }}
                    >
                      {role.display_name || role.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePage}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const PageTable = ({ pages, onEdit, onToggle, roles }) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Status</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Path</TableHead>
          <TableHead>Order</TableHead>
          <TableHead>Access</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pages.map(page => (
          <TableRow key={page.page_key}>
            <TableCell>
              <Badge variant={page.is_enabled ? 'default' : 'secondary'}>
                {page.is_enabled ? (
                  <><Eye className="h-3 w-3 mr-1" /> Enabled</>
                ) : (
                  <><EyeOff className="h-3 w-3 mr-1" /> Disabled</>
                )}
              </Badge>
            </TableCell>
            <TableCell className="font-medium">{page.title}</TableCell>
            <TableCell className="text-sm text-muted-foreground font-mono">{page.path}</TableCell>
            <TableCell>{page.order}</TableCell>
            <TableCell>
              {page.allowed_roles && page.allowed_roles.length > 0 ? (
                <Badge variant="outline" className="text-xs">
                  {page.allowed_roles.length} role(s)
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">All roles</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onToggle(page)}
                >
                  {page.is_enabled ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onEdit(page)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
